import React, { useState, useRef, useEffect } from "react";
import Modal from "antd/lib/modal";
import notification from "antd/lib/notification";
import { Button } from "components/Common/Button";
import { Paragraph2 } from "themes/default/_typography";
import { useDispatch, useSelector } from "react-redux";
import { CloseIcon } from "components/Icons";

import { selectImageCropAspectRatio } from "store/selectors";
import { updateImageCropAspectRatio } from "store/slices";
import { decimalToFraction, calculateMinCropBoxSize, minimumPixelSize } from "utils";

import { Cropper } from "react-cropper";
import "cropperjs/dist/cropper.css";

const CropModal = (props) => {
	const { localFile, onSave, onCancel, updateFileAddedLocally, sizeLimit, open, handleClose } = props;
	const cropperRef = useRef(null);
	const [croppedBlob, setCroppedBlob] = useState(null);
	const [minCropWidth, setMinCropWidth] = useState(0);
  	const [minCropHeight, setMinCropHeight] = useState(0);
	const [isLoadedImage, setIsLoadedImage] = useState(false);
	const [isCropping, setIsCropping] = useState(false);

	const onCrop = () => {
		setIsCropping(true);
		const imageElement = cropperRef?.current;
		const cropper = imageElement?.cropper;
		const canvas = cropper.getCroppedCanvas();

		if (!canvas) {
			setIsCropping(false);
			return;
		}

		canvas.toBlob((blob) => {
			if (blob !== null) {
				setCroppedBlob(blob);
			}

			setIsCropping(false);
		}, "image/jpeg");
	};

	const dispatch = useDispatch();
	const aspectRatio = useSelector(selectImageCropAspectRatio);

	const handleCancel = () => {
		//Should dispatch action to close modal
		handleClose();
		dispatch(updateImageCropAspectRatio(null));
		if (onCancel) {
			onCancel();
		}
	};

	const handleSave = () => {
		if (!croppedBlob) {
			setCroppedBlob(null);
			notification.error("No cropped image available. Please crop your image before saving.");
		} else if (sizeLimit?.limit && croppedBlob?.size > sizeLimit?.limit) {
			setCroppedBlob(null);
			notification.error(sizeLimit?.error);
		} else {
			const preview = URL.createObjectURL(croppedBlob);
			updateFileAddedLocally(preview);
			onSave(croppedBlob);
		}

		handleClose();
	};

	const { numerator: width, denominator: height } = minimumPixelSize(aspectRatio || 5 / 3);
	const minimumSize = `${width}px X ${height}px`;

	useEffect(() => {
		setIsLoadedImage(false);
		const img = new window.Image(); // to get image properties (width and height)
        img.src = localFile;
        img.onload = () => {
          	const width = img.naturalWidth;
          	const height = img.naturalHeight;
          	const {minCropBoxHeight, minCropBoxWidth} = calculateMinCropBoxSize(width, height, aspectRatio || 5 / 3);
		  	setMinCropHeight(minCropBoxHeight);
          	setMinCropWidth(minCropBoxWidth);
			setIsLoadedImage(true);
        };
	}, [localFile, aspectRatio])

	return (
		<Modal
			afterClose={() => {}}
			destroyOnClose={true}
			className="sb-modal"
			title="Crop Your Image"
			open={open}
			closeIcon={<CloseIcon data-id="close-category-modal" />}
			maskClosable={false}
			onCancel={handleCancel}
			data-id="img-crop-modal"
			footer={[
				<div key="create" className="sb-create-category-modal__footer">
					<Button
						data-id="sb-modal-cancel"
						className="primary-outlined primary-outlined--delete"
						onClick={handleCancel}
					>
						Cancel
					</Button>

					<Button
						className="primary-filled"
						onClick={handleSave}
						data-id="sb-modal-confirm"
						disabled={isCropping || !croppedBlob}
					>
						{isCropping ? "Processing..." : "Save"}
					</Button>
				</div>,
			]}
		>
			<div className="sb-create-category-modal" data-id="category-modal">
				<Paragraph2>Cropping Ratio: {decimalToFraction(aspectRatio || 5 / 3)}</Paragraph2>
				<Paragraph2>Minimum size: {minimumSize}</Paragraph2>
				{
					isLoadedImage &&
					<Cropper
						src={localFile}
						crop={onCrop}
						ref={cropperRef}
						aspectRatio={aspectRatio}
						minCropBoxWidth={minCropWidth}
						minCropBoxHeight={minCropHeight}
						zoom={(e) => {
      						e.preventDefault(); // cancel zoom
  						}}
						style={{
							height: 300,
							width: "100%",
							backgroundColor: "white",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							backgroundSize: "cover",
							backgroundPosition: "center",
						}}
					/>
				}
			</div>
		</Modal>
	);
};

export default CropModal;

